<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { MDCTextField } from '@material/textfield';
  import { fly, fade } from 'svelte/transition';
  import { get_opt_id } from '../utils';
  import { TS } from '../../config/translation-keys.js';
  import { t9n } from '../translate.js';

  export let id;
  export let label;
  export let items;
  export let value;
  export let valid = true;
  export let invalid_text;
  export let disabled = false;
  export let selecting = false;
  export let showed_elements = 3;
  export let variant = 'outlined';
  export let auto_complete = false;
  export let show_hover_tooltip = false;
  export let input_value = null;
  export let height;
  export let width;
  export let margin_left;
  export let margin_bottom;
  export let is_required = false;
  export let has_focused = false;
  export let list_state = 'default';
  export let max_length = null;

  let focus = false;
  let inner_items;
  let text_field;
  let is_mouse_on_container = false;
  let has_init = false;
  let has_input_value_loaded = false;
  let searched_items;
  let is_key_delete = false;
  let hovered_element;

  const init_key_listener = () => {
    if (auto_complete) {
      document.addEventListener('keydown', function(event) {
        const key = event.key;
        is_key_delete = key === 'Backspace' || key === 'Delete';
      });
    }
  };

  const init_selector = () => {
    let element = document.getElementById(`${id}-select`);
    if(!!element) text_field = new MDCTextField(element);
    has_init = true;
    init_key_listener();

  };

  const on_focus = () => {
    focus = !focus;
    if (!selecting && focus) selecting = true;
    if (selecting && !focus && !is_mouse_on_container) selecting = false;
  };

  const init_items = () => {
    inner_items = items;
    inner_items.forEach(e => (e.show = true));
    searched_items = inner_items.length;
  };

  const on_mouse_over = (value, index) => {
    is_mouse_on_container = value;
    hovered_element = index;
  };

  const select = index => {
    input_value = items[index].name;
    value = items[index].val;
    text_field.focus();
    selecting = false;
  };

  const field_on_click = () => {
    if (focus && !selecting) selecting = true;
    has_focused = true;
  };

  const init_value = () => {
    items.forEach(e => {
      if (value === e.val) input_value = e.name;
    });
    has_input_value_loaded = true;
  };

  const search_auto_complete = input => {
    let showed_elements = 0;
    inner_items.forEach(e => {
      if (e.show) showed_elements++;
    });
    if (auto_complete && !is_key_delete && showed_elements === 1) {
      let index = inner_items.findIndex(e => e.show === true);
      if (index !== -1) {
        select(index);
      }
    }
  };

  const search = input_value => {
    searched_items = 0;
    let searched = input_value.target.value.toLowerCase();
    inner_items.forEach(e => {
      let item_name = e.name.toLowerCase();
      e.show = item_name.includes(searched);
      if (e.show) searched_items++;
    });

    if (auto_complete) search_auto_complete(searched);
  };

  $: if (!!value && has_init && !has_input_value_loaded) {
    init_value();
  }

  $: if (items) init_items();

  $: if (items.length === 0 && has_init) {
    disabled = true;
  }

  $: if(input_value){
    if(max_length !== null){
      if(input_value.toString().length > parseInt(max_length)){
        if(!selecting){

          selecting = true;
        }
        let val = input_value.toString().slice(0, parseInt(max_length));
        input_value = parseInt(val);
        value = input_value;
      }
    }
  }

  $: console.log(label, disabled)

  onMount(init_selector);
</script>

<div class="position-relative" class:disabled-bg={disabled && list_state === 'default'}>
  {#if variant === 'filled'}
    <label
      id={`${id}-select`}
      data-cy={`${id}-select`}
      class="mdc-text-field mdc-text-field--filled "
      class:mdc-text-field--invalid={!valid}
      class:mdc-text-field--label-floating={!!value}
      class:mdc-text-field--disabled={disabled}
      style={`height: ${height}; width: ${width}; margin-left: ${margin_left}; margin-bottom: ${margin_bottom}`}>
      <span class="mdc-text-field__ripple" />
      <span
        class="mdc-floating-label"
        id={`${id}-label`}
        class:mdc-floating-label--float-above={!!value}>
        {label}
        {#if is_required}*{/if}
      </span>
      <input
        id={`${id}-input`}
        autocomplete="new-password"
        class="mdc-text-field__input"
        type="text"
        aria-labelledby={`${id}-label`}
        on:focus={on_focus}
        on:blur={on_focus}
        on:click={field_on_click}
        on:input={search}
        bind:value={input_value}
        {disabled} />
      <span class="mdc-line-ripple" />
    </label>
  {:else}
    <label
      id={`${id}-select`}
      data-cy={`${id}-select`}
      class="mdc-text-field mdc-text-field--outlined
      mdc-text-field--with-trailing-icon"
      class:mdc-text-field--invalid={!valid}
      class:mdc-text-field--label-floating={!!value}
      class:mdc-text-field--disabled={disabled}
      style={`height: ${height}; width: ${width}; margin-left: ${margin_left}; margin-bottom: ${margin_bottom}`}>
      <span class="mdc-notched-outline">
        <span class="mdc-notched-outline__leading" />
        <span class="mdc-notched-outline__notch">
          <span
            class="mdc-floating-label"
            id={`${id}-label`}
            class:mdc-floating-label--float-above={!!value}>
            {label}
            {#if is_required}*{/if}
          </span>
        </span>
        <span class="mdc-notched-outline__trailing" />
      </span>

      <input
        id={`${id}-input`}
        class="mdc-text-field__input"
        type="text"
        autocomplete="new-password"
        aria-labelledby={`${id}-label`}
        on:focus={on_focus}
        on:blur={on_focus}
        on:click={field_on_click}
        on:input={search}
        bind:value={input_value}
        {disabled} />
      {#if !selecting}
        <i
          class="material-icons mdc-text-field__icon"
          transition:fade={{ duration: 100 }}>
          arrow_drop_down
        </i>
      {:else}
        <i
          class="material-icons mdc-text-field__icon"
          transition:fade={{ duration: 100 }}>
          arrow_drop_up
        </i>
      {/if}

    </label>
  {/if}

  {#if selecting}
    <div
      id={`${id}-options`}
      class="list-container"
      style={`height: ${showed_elements * 56}px`}
      transition:fly={{ y: -50, duration: 250 }}
      class:container-style-outlined={variant === 'outlined'}
      class:container-style-filled={variant === 'filled'}
      class:container-style-filled-valid={variant === 'filled' && valid}
      class:container-style-outlined-invalid={variant === 'outlined' && !valid}
      class:container-style-filled-invalid={variant === 'filled' && !valid}>
      {#each inner_items as item, index}
        {#if searched_items === 0 && index === 0}
          <div
            class="list-container-elem"
            id="opt-no-result"
            transition:fade={{ duration: 100 }}
            class:list-container-elem-outlined={variant === 'outlined'}
            class:list-container-elem-filled={variant === 'filled'}>
            {t9n(TS.TABLE_NO_RESULT)}
          </div>
        {:else if item.show}
          <div
            class="list-container-elem list-container-elem-cursor"
            id={`opt-${get_opt_id(item.name)}`}
            transition:fade={{ duration: 100 }}
            class:list-container-elem-outlined={variant === 'outlined'}
            class:list-container-elem-filled={variant === 'filled'}
            on:mouseover={() => on_mouse_over(true, index)}
            on:mouseout={() => on_mouse_over(false, index)}
            on:click|stopPropagation|preventDefault={() => select(index)}>
            {item.name}
          </div>
        {/if}
      {/each}
    </div>
  {/if}

  {#if !valid && !!invalid_text}
    <div class="tooltip-error">
      <span class="tooltip-text tooltip-top" id={`${id}-invalid`}>
        {invalid_text}
      </span>
    </div>
  {/if}

  {#if is_mouse_on_container && show_hover_tooltip && selecting && hovered_element !== searched_items}
    <div class="tooltip-default">
      <span class="tooltip-text tooltip-top">
        {#if !!items[hovered_element].description}
          {items[hovered_element].description}
        {:else}-{/if}
      </span>
    </div>
  {/if}
</div>

<style>
    .mdc-text-field:not(.mdc-text-field--disabled) .mdc-floating-label {
        background-color: white !important;
    }
  .mdc-notched-outline__notch {
    width: initial !important;
  }
  .disabled-bg {
    opacity: 50%;
  }

  .tooltip-default {
    position: relative;
    font-size: 13px;
    top: -67px;
    animation: transition_in 0.6s;
  }

  .tooltip-default .tooltip-top {
    position: absolute;
    z-index: 5;
    bottom: 100%;
    right: 0;
    left: 20%;
  }

  .tooltip-default .tooltip-text {
    background-color: var(--mdc-theme-secondary);
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 8px 8px;
    width: 77%;
  }

  .tooltip-error {
    position: relative;
    font-size: 12px;
    top: -50px;
    z-index: 12;
      opacity: 0;
      animation: transition_in 0.5s;
      animation-delay: 2s;
      animation-fill-mode: forwards;
  }

  .tooltip-error .tooltip-top {
    position: absolute;
    z-index: 1;
    bottom: 100%;
    left: 50%;
    right: 0;
  }

  .tooltip-error .tooltip-text {
    background-color: var(--invalid-color);
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 8px 8px;
  }

  .mdc-text-field--outlined
    .mdc-notched-outline--upgraded
    .mdc-floating-label--float-above {
    background-color: white;
    padding-left: 2px;
    padding-right: 2px;
  }

  .position-relative {
    position: relative;
    width: inherit;
  }

  .mdc-text-field {
    width: inherit;
  }

  .list-container {
    position: absolute;
    top: 57px;
    width: 99%;
    z-index: 10000;
    overflow-y: scroll;
    overflow-x: hidden;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2),
      0 5px 8px 0 rgba(0, 0, 0, 0.14), 0 1px 14px 0 rgba(0, 0, 0, 0.12);
  }

  .container-style-outlined {
    background-color: white;
    border-radius: 5px;
  }

  .container-style-outlined-invalid {
    border: 2px solid var(--invalid-color);
  }

  .container-style-filled {
    background-color: #ececec;
    border-radius: 5px;
  }

  .container-style-filled-valid {
    border-bottom: 2px solid #006699;
  }

  .container-style-filled-invalid {
    border-bottom: 2px solid var(--invalid-color);
  }

  .list-container-elem {
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 16px;
    padding-bottom: 16px;
    width: inherit;
    min-height: 20px;
    display: flex;
  }

  .list-container-elem-cursor {
    cursor: pointer;
  }

  .list-container-elem-outlined:hover {
    background-color: #ececec;
  }

  .list-container-elem-filled:hover {
    background-color: #dcdcdc;
  }



    @media (max-width: 768px) {
        .list-container{
            max-height: 55px;
        }
    }
</style>
